import {
  Activity, Building2, CalendarCheck, CalendarDays, CalendarRange, ChartColumn, ClipboardCheck,
  Clock, GitBranch, LayoutGrid, LayoutTemplate, ListTodo, ScrollText, Settings, ShieldCheck,
  UserCheck, Users, Wallet, type LucideIcon,
} from 'lucide-react';
import type { Permission } from '@pm/shared';
import type { Session } from '@/features/auth/session';

export interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  /** Hidden unless the user holds at least one of these. */
  permissions: Permission[];
  /** Also shown when this holds - for access that comes from a row, not a grant. */
  also?: (session: Session) => boolean;
  end?: boolean;
}

export interface NavSection {
  labelKey: string;
  items: NavItem[];
}

/**
 * Grouped by *whose data it is*, not by which module implements it.
 *
 * The previous grouping put "Attendance" (my own) next to "Employees"
 * (everyone's) under one HR heading, so nothing on screen told an employee
 * which pages were theirs and which were the company's. Now the first
 * section is always personal, the middle sections are the work, and anything
 * that governs other people sits under Team or Administration - which for an
 * employee are empty and therefore hidden entirely.
 *
 * Every item is permission-gated and a section with no visible items is
 * dropped, so each role gets a sidebar that reads as if it were built for it.
 */
export const NAV: NavSection[] = [
  {
    labelKey: 'nav.sections.myWork',
    items: [
      { to: '/my-day', labelKey: 'nav.myDay', icon: ListTodo, permissions: ['time.track'] },
      { to: '/my-tasks', labelKey: 'nav.myTasks', icon: ClipboardCheck, permissions: ['pm.item.update'] },
      { to: '/hr/attendance', labelKey: 'nav.myAttendance', icon: Clock, permissions: ['hr.attendance.self'], end: true },
      { to: '/hr/leave', labelKey: 'nav.myLeave', icon: CalendarDays, permissions: ['hr.leave.request'], end: true },
      { to: '/hr/holidays', labelKey: 'nav.holidays', icon: CalendarRange, permissions: ['hr.attendance.self'] },
    ],
  },
  {
    labelKey: 'nav.sections.projects',
    items: [
      { to: '/', labelKey: 'nav.workspaces', icon: LayoutGrid, permissions: ['pm.workspace.view'], end: true },
      { to: '/templates', labelKey: 'nav.templates', icon: LayoutTemplate, permissions: ['pm.board.view'] },
      { to: '/reports', labelKey: 'nav.reports', icon: ChartColumn, permissions: ['pm.report.view'] },
      { to: '/automations', labelKey: 'nav.automations', icon: GitBranch, permissions: ['pm.board.manage'] },
    ],
  },
  {
    // Only appears for someone who can see beyond their own record.
    labelKey: 'nav.sections.team',
    items: [
      { to: '/hr/employees', labelKey: 'nav.employees', icon: Users, permissions: ['hr.employee.view_team'] },
      {
        to: '/hr/departments', labelKey: 'nav.departments', icon: Building2,
        permissions: ['hr.departments.manage'], also: (session) => session.leadsDepartment,
      },
      { to: '/hr/attendance?view=team', labelKey: 'nav.teamAttendance', icon: UserCheck, permissions: ['hr.attendance.view_team'] },
      { to: '/hr/leave?view=queue', labelKey: 'nav.leaveApprovals', icon: CalendarCheck, permissions: ['hr.leave.approve'] },
      { to: '/hr/balances', labelKey: 'nav.balances', icon: Wallet, permissions: ['hr.leave.manage_balances', 'time.view_team'] },
    ],
  },
  {
    labelKey: 'nav.sections.administration',
    items: [
      { to: '/hr/settings', labelKey: 'nav.hrSettings', icon: Settings, permissions: ['hr.settings.manage'] },
      { to: '/admin/roles', labelKey: 'nav.roles', icon: ShieldCheck, permissions: ['admin.roles.view'] },
      { to: '/admin/audit', labelKey: 'nav.audit', icon: ScrollText, permissions: ['admin.audit.view'] },
      { to: '/admin/diagnostics', labelKey: 'nav.diagnostics', icon: Activity, permissions: ['admin.logs.view'] },
    ],
  },
];
